/**
 * ヘルスビュー
 *
 * Garminからの健康データをダッシュボード形式で表示
 * フルアクセス権限を持つユーザーのみ表示
 * 日付選択はActivityPageのHeaderで行う
 */

import { Activity, Flame, Footprints, Timer } from 'lucide-react'

import { BodyMetricsSummary } from '@/components/shared/body-metrics-summary'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useAppStore } from '@/store/useAppStore'
import { useGarminStore } from '@/store/useGarminStore'

import { ActivityCard } from './activity-card'
import { BodyBatteryGauge } from './body-battery-gauge'
import { HeartRateCard } from './heart-rate-card'
import { ProgressRing } from './progress-ring'
import { SleepBatteryChart } from './sleep-battery-chart'
import { SleepCard } from './sleep-card'
import { StressCard } from './stress-card'

type Props = {
  selectedDateStr: string
}

export const HealthView = ({ selectedDateStr }: Props) => {
  const hasFullAccess = useAppStore((state) => state.hasFullAccess)
  const { dashboard, isDashboardLoading, fetchDashboard, error, clearError } = useGarminStore()

  // フルアクセスがない場合は非表示
  if (!hasFullAccess) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground gap-4 p-8">
        <Activity className="size-16 opacity-50" />
        <p>この機能は利用できません</p>
      </div>
    )
  }

  // ローディング
  if (isDashboardLoading && !dashboard) {
    return (
      <div className="p-4 space-y-4">
        <div className="flex justify-center gap-4">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="size-32 rounded-full" />
          ))}
        </div>
        <div className="grid grid-cols-2 gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-40" />
          ))}
        </div>
      </div>
    )
  }

  // エラー
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground gap-4 p-8">
        <Activity className="size-16 opacity-50" />
        <p>データの取得に失敗しました</p>
        <p className="text-sm">{error}</p>
        <Button
          variant="outline"
          onClick={() => {
            clearError()
            fetchDashboard(selectedDateStr)
          }}
        >
          再試行
        </Button>
      </div>
    )
  }

  return (
    <div className="p-4 space-y-6 pb-20">
      {/* メインリング（歩数、アクティブ時間、カロリー） */}
      <div className="flex justify-around items-center py-4">
        <ProgressRing
          value={dashboard?.steps ?? 0}
          max={dashboard?.steps_goal ?? 10000}
          size={100}
          strokeWidth={8}
          color="hsl(142 76% 36%)"
          icon={<Footprints className="size-4 text-green-600" />}
          label="歩数"
        />
        <ProgressRing
          value={dashboard?.active_minutes ?? 0}
          max={60}
          size={100}
          strokeWidth={8}
          color="hsl(221 83% 53%)"
          icon={<Timer className="size-4 text-blue-600" />}
          unit="分"
          label="アクティブ"
        />
        <ProgressRing
          value={dashboard?.active_calories ?? 0}
          max={500}
          size={100}
          strokeWidth={8}
          color="hsl(25 95% 53%)"
          icon={<Flame className="size-4 text-orange-600" />}
          label="カロリー"
        />
      </div>

      {/* 距離・階段 */}
      <div className="grid grid-cols-2 gap-4 text-center">
        <div className="rounded-xl border bg-card p-3">
          <p className="text-2xl font-bold tabular-nums">{dashboard?.distance_km ?? 0}</p>
          <p className="text-xs text-muted-foreground">km</p>
        </div>
        <div className="rounded-xl border bg-card p-3">
          <p className="text-2xl font-bold tabular-nums">{dashboard?.floors_climbed ?? 0}</p>
          <p className="text-xs text-muted-foreground">階</p>
        </div>
      </div>

      {/* 体組成サマリ (筋トレ/栄養 AI 連携) */}
      <BodyMetricsSummary title="体組成 (AI連携用)" />
      {/* メトリクスカード */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <HeartRateCard heartRate={dashboard?.heart_rate ?? null} />
        <BodyBatteryGauge
          current={dashboard?.body_battery?.current ?? null}
          highest={dashboard?.body_battery?.highest ?? null}
          lowest={dashboard?.body_battery?.lowest ?? null}
          charged={dashboard?.body_battery?.charged ?? null}
          drained={dashboard?.body_battery?.drained ?? null}
        />
        <SleepCard sleep={dashboard?.sleep ?? null} />
        <StressCard stress={dashboard?.stress ?? null} />
      </div>

      {/* 睡眠 & Body Battery 複合チャート */}
      <SleepBatteryChart sleep={dashboard?.sleep ?? null} bodyBattery={dashboard?.body_battery ?? null} />

      {/* 最近のアクティビティ */}
      {dashboard?.recent_activities && dashboard.recent_activities.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-medium text-muted-foreground">最近のアクティビティ</h3>
          {dashboard.recent_activities.map((activity) => (
            <ActivityCard key={activity.activity_id} activity={activity} />
          ))}
        </div>
      )}
    </div>
  )
}
